import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TbPlayerPlayFilled } from "react-icons/tb";
import PageHeader from "@/components/app-shell/PageHeader";
import { CANVAS_COVER_DOTS_STYLE } from "@/lib/canvasCover";
import { cn } from "@/lib/utils";

/**
 * Les tutoriels — pas encore écrits. La page existe déjà pour que l'entrée de
 * la sidebar mène quelque part qui ait l'air voulu, et pour annoncer ce qui
 * viendra plutôt que d'afficher un vide.
 */
export const Route = createFileRoute("/_app/tutorials")({
  component: TutorialsPage,
});

// Des clés, pas des libellés : `t()` est appelé au render.
const UPCOMING = [
  {
    key: "firstCanvas",
    tint: "bg-blue-50",
    icon: "text-blue-600",
  },
  {
    key: "withNole",
    tint: "bg-violet-50",
    icon: "text-violet-600",
  },
  {
    key: "tablesApps",
    tint: "bg-teal-50",
    icon: "text-teal-700",
  },
  {
    key: "connectAssistants",
    tint: "bg-orange-50",
    icon: "text-orange-700",
  },
  {
    key: "capture",
    tint: "bg-green-50",
    icon: "text-green-700",
  },
  {
    key: "shortcuts",
    tint: "bg-slate-100",
    icon: "text-slate-600",
  },
] as const;

function ComingSoon() {
  const { t } = useTranslation("shell");
  return (
    <span className="text-xs font-semibold text-slate-500">
      {t("tutorials.comingSoon")}
    </span>
  );
}

function TutorialsPage() {
  const { t } = useTranslation(["shell", "common"]);
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t("tutorials.title")}
        subtitle={t("tutorials.subtitle")}
      />

      <div className="flex gap-6 rounded-2xl border border-violet-100 bg-violet-50/40 p-5 max-md:flex-col md:p-6">
        <div
          className="flex h-44 shrink-0 items-center justify-center rounded-xl bg-violet-100 md:w-80"
          style={CANVAS_COVER_DOTS_STYLE}
        >
          <span className="flex size-14 items-center justify-center rounded-full bg-violet-600 text-white shadow-lg">
            <TbPlayerPlayFilled className="size-6" />
          </span>
        </div>
        <div className="flex flex-col justify-center gap-2">
          <span className="self-start rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-bold text-violet-700">
            {t("tutorials.featured.badge")}
          </span>
          <h2 className="text-xl font-bold text-slate-900">
            {t("tutorials.featured.title")}
          </h2>
          <p className="max-w-md text-sm text-slate-600">
            {t("tutorials.featured.description")}
          </p>
          <ComingSoon />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {UPCOMING.map((tutorial) => (
          <div
            key={tutorial.key}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-surface"
          >
            <div
              className={cn("flex h-28 items-center justify-center", tutorial.tint)}
              style={CANVAS_COVER_DOTS_STYLE}
            >
              <span
                className={cn(
                  "flex size-10 items-center justify-center rounded-full bg-surface shadow-sm",
                  tutorial.icon,
                )}
              >
                <TbPlayerPlayFilled className="size-4" />
              </span>
            </div>
            <div className="flex flex-col gap-1.5 px-4 pt-3.5 pb-4">
              <h3 className="font-bold text-slate-900">
                {t(`tutorials.items.${tutorial.key}.title`)}
              </h3>
              <p className="text-sm text-slate-600">
                {t(`tutorials.items.${tutorial.key}.description`)}
              </p>
              <ComingSoon />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
